import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Star, ShieldCheck, Anchor, MapPin, Mail, Compass, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import Avatar from "../components/Avatar";
import TripCard from "../components/TripCard";
import FounderBadge from "../components/FounderBadge";

export default function CaptainProfile() {
  const { userId } = useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["captain", userId],
    queryFn: async () => (await api.get(`/captains/${userId}`)).data,
  });
  const { data: reviews = [] } = useQuery({
    queryKey: ["captain-reviews", userId],
    queryFn: async () => (await api.get(`/captains/${userId}/reviews`)).data,
  });

  useEffect(() => {
    if (data?.captain?.name) document.title = `${data.captain.name} — Le Bon Matelot`;
  }, [data]);

  if (isLoading) return <div className="max-w-5xl mx-auto px-6 py-24 text-center text-bretagne">Chargement…</div>;
  if (!data) return <div className="max-w-5xl mx-auto px-6 py-24 text-center">Capitaine introuvable.</div>;

  const c = data.captain;
  const trips = data.active_trips || [];
  const stats = data.stats || {};
  const ratingValue = (c.rating || 0).toFixed(1);

  return (
    <div className="bg-blanc min-h-screen">
      {/* Header */}
      <div className="relative bg-gradient-to-br from-ocean to-bretagne text-blanc overflow-hidden">
        <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-corail/30 blur-3xl" />
        <div className="absolute -top-20 -left-10 w-72 h-72 rounded-full bg-turquoise/30 blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-6 pt-8 pb-14">
          <Link to="/explorer" className="inline-flex items-center gap-1.5 text-sm text-blanc/80 hover:text-blanc font-semibold" data-testid="captain-back">
            <ArrowLeft className="w-4 h-4" /> Retour
          </Link>
          <div className="mt-6 flex flex-col md:flex-row items-start md:items-center gap-6">
            <Avatar name={c.name} picture={c.picture} size={112} ring />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-jaune font-bold">Capitaine</div>
              <h1 className="font-display text-3xl md:text-4xl font-bold mt-1" data-testid="captain-name">{c.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {c.home_port || "Port à définir"}</span>
                <span className="inline-flex items-center gap-1.5"><Anchor className="w-3.5 h-3.5" /> {stats.total_trips || 0} sortie{(stats.total_trips || 0) > 1 ? "s" : ""}</span>
                {stats.reviews_count > 0 ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5 fill-jaune text-jaune" /> {ratingValue} · {stats.reviews_count} avis
                  </span>
                ) : (
                  <span className="text-blanc/70">Pas encore d'avis</span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <FounderBadge show={c.is_founder} variant="solid" />
                {c.verified_email && (
                  <span className="inline-flex items-center gap-1 bg-blanc/15 backdrop-blur text-blanc text-xs font-bold px-2.5 py-1 rounded-full" data-testid="badge-email-verified">
                    <Mail className="w-3 h-3" /> Email vérifié
                  </span>
                )}
                {c.verified_captain && (
                  <Link to="/confiance" className="inline-flex items-center gap-1 bg-corail text-blanc text-xs font-bold px-2.5 py-1 rounded-full hover:bg-corail/90 transition">
                    <ShieldCheck className="w-3 h-3" /> Capitaine vérifié
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Bio */}
        {c.bio && (
          <section className="mb-12">
            <h2 className="font-display text-xl font-bold text-ocean mb-2">À propos</h2>
            <p className="text-ocean/80 leading-relaxed max-w-3xl whitespace-pre-line" data-testid="captain-bio">{c.bio}</p>
          </section>
        )}

        {/* Active trips */}
        <section>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-xl font-bold text-ocean inline-flex items-center gap-2">
              <Compass className="w-5 h-5 text-corail" /> Sorties proposées
            </h2>
            <span className="text-sm text-bretagne">{trips.length} active{trips.length > 1 ? "s" : ""}</span>
          </div>
          {trips.length === 0 ? (
            <div className="bg-sable border border-brume rounded-2xl p-8 text-center">
              <p className="text-ocean/70">Aucune sortie proposée pour le moment.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="captain-trips">
              {trips.map((t) => (<TripCard key={t.trip_id} trip={t} />))}
            </div>
          )}
        </section>

        {/* Reviews */}
        <section className="mt-14">
          <h2 className="font-display text-xl font-bold text-ocean mb-4">Avis</h2>
          {reviews.length === 0 ? (
            <div className="bg-sable border border-brume rounded-2xl p-6 text-sm text-ocean/70" data-testid="captain-no-reviews">
              Pas encore d'avis. Soyez le premier à embarquer.
            </div>
          ) : (
            <ul className="space-y-3">
              {reviews.slice(0, 10).map((r) => (
                <li key={r.review_id} className="bg-blanc border border-brume rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-ocean">{r.reviewer_name}</div>
                    <div className="inline-flex items-center gap-0.5 text-sm text-ocean">
                      <Star className="w-3.5 h-3.5 fill-jaune text-jaune" /> {r.rating}/5
                    </div>
                  </div>
                  {r.comment && <p className="text-sm text-ocean/80 mt-1.5 leading-relaxed">{r.comment}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
